import useSWR from 'swr';
import { supabase } from '../lib/supabase';

// Type definition for learning article data structure
export interface LearningArticle {
  id: string;
  title: string;
  summary?: string;
  content: string;
  author?: string;
  cover_image?: string;
  read_count: number;
  like_count: number;
  created_at: string;
  updated_at: string;
  domain_id?: string;
  subject_id?: string;
  point_id?: string;
}

// Configuration options for the hook
export interface UseLearningArticlesOptions {
  limit?: number;
  domain_id?: string; // 按思维领域筛选
  subject_id?: string; // 按学科筛选
  point_id?: string; // 按知识点筛选
  sortBy?: 'created_at' | 'updated_at' | 'read_count' | 'like_count' | 'title'; // 排序字段
  sortOrder?: 'asc' | 'desc'; // 排序方向
  refreshInterval?: number;
  revalidateOnFocus?: boolean;
  revalidateOnReconnect?: boolean;
  suspense?: boolean;
}

// Hook return type
export interface UseLearningArticlesReturn {
  data: LearningArticle[] | undefined;
  error: Error | undefined;
  isLoading: boolean;
  isValidating: boolean;
  mutate: () => Promise<LearningArticle[] | undefined>;
  revalidate: () => Promise<LearningArticle[] | undefined>;
}

// Fetcher function for SWR
const fetchLearningArticles = async (url: string): Promise<LearningArticle[]> => {
  console.log('🚀 开始获取学习文章数据:', url);
  try {
    // 获取当前用户的session token（如果有的话，用于个性化功能）
    const { data: { session } } = await supabase.auth.getSession();
    
    console.log('📡 发送请求到:', url);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        // 使用anon key，如果用户已登录则附加access_token用于个性化功能
        'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
    });

    console.log('📨 响应状态:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API错误响应:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const responseData = await response.json();
    console.log('✅ 成功获取数据:', responseData);
    
    // Handle standardized response format
    let data;
    if (responseData && typeof responseData === 'object' && 'success' in responseData) {
      // Standardized response format: { success: true, data: [...] } or { success: true, data: { data: [...], total, page, limit, totalPages } }
      if (!responseData.success) {
        throw new Error(responseData.error || 'API request failed');
      }
      
      // 检查是否是嵌套的分页响应格式
      if (responseData.data && typeof responseData.data === 'object' && 'data' in responseData.data && Array.isArray(responseData.data.data)) {
        // 嵌套格式：{ success: true, data: { data: [...], total, page, limit, totalPages } }
        data = responseData.data.data;
        console.log('✅ 检测到分页响应格式，总共', responseData.data.total, '条记录');
      } else if (Array.isArray(responseData.data)) {
        // 直接数组格式：{ success: true, data: [...] }
        data = responseData.data;
      } else {
        console.error('❌ 响应数据格式错误:', { responseData });
        throw new Error('Invalid response format: data field should be array or pagination object');
      }
    } else {
      // Direct array response (fallback for backward compatibility)
      data = responseData;
    }
    
    // Validate the actual data structure
    if (!Array.isArray(data)) {
      console.error('❌ 响应数据格式错误:', { responseData, extractedData: data });
      throw new Error('Invalid response format: expected array in data field');
    }

    console.log('✅ 解析后的数据数组:', data.length, '条记录');
    return data;
  } catch (error) {
    console.error('❌ 获取学习文章失败:', error);
    console.error('错误详情:', error instanceof Error ? error.message : error);
    throw error;
  }
};

/**
 * Custom hook for fetching learning articles with SWR
 * 
 * Features:
 * - Automatic caching and revalidation
 * - Background updates
 * - Error handling
 * - Loading states
 * - Manual revalidation for CSR and SSR
 * - Configurable options
 * 
 * @param options Configuration options for the hook
 * @returns Hook state and methods
 */
export const useLearningArticles = (
  options: UseLearningArticlesOptions = {}
): UseLearningArticlesReturn => {
  console.log('🎣 useLearningArticles hook初始化', options);
  
  const {
    limit = 5,
    domain_id,
    subject_id, 
    point_id,
    sortBy = 'created_at',
    sortOrder = 'desc',
    refreshInterval = 0, // 禁用自动刷新，减少不必要的请求
    revalidateOnFocus = false, // 禁用焦点时重新验证
    revalidateOnReconnect = true, // 保留网络重连时的重新验证
    suspense = false,
  } = options;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  console.log('🔗 Supabase URL:', supabaseUrl ? '已配置' : '未配置');
  
  // Construct the API URL - 使用公共API，普通用户也可以访问
  const apiUrl = supabaseUrl 
    ? (() => {
        const url = new URL(`${supabaseUrl}/functions/v1/public-learning-content`);
        url.searchParams.set('action', 'articles');
        url.searchParams.set('limit', limit.toString());
        
        // 添加筛选参数
        if (domain_id) url.searchParams.set('domain_id', domain_id);
        if (subject_id) url.searchParams.set('subject_id', subject_id);
        if (point_id) url.searchParams.set('point_id', point_id);
        
        // 添加排序参数
        url.searchParams.set('sort', sortBy);
        url.searchParams.set('order', sortOrder);
        
        return url.toString();
      })()
    : null;
    
  console.log('📡 API URL:', apiUrl);

  // Use SWR with the fetcher function
  const {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR<LearningArticle[], Error>(
    apiUrl, // Key - will be null if supabaseUrl is not available
    fetchLearningArticles,
    {
      refreshInterval,
      revalidateOnFocus,
      revalidateOnReconnect,
      suspense,
      // 添加重试配置
      errorRetryCount: 2,
      errorRetryInterval: 2000,
      // 添加去重配置，防止重复请求
      dedupingInterval: 10000, // 10秒内相同请求去重
      onError: (error) => {
        console.error('SWR Error in useLearningArticles:', error);
      },
      onSuccess: (data) => {
        console.log('Successfully fetched learning articles:', data?.length, 'items');
      },
      // Add fallback data structure
      fallbackData: undefined,
      // Keep previous data while revalidating
      keepPreviousData: true,
    }
  );

  // Manual revalidation function (useful for CSR and SSR)
  const revalidate = async (): Promise<LearningArticle[] | undefined> => {
    try {
      console.log('Manual revalidation triggered for learning articles');
      const result = await mutate();
      return result;
    } catch (error) {
      console.error('Manual revalidation failed:', error);
      throw error;
    }
  };

  return {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
    revalidate,
  };
};

/**
 * Static function to prefetch learning articles data (useful for SSR)
 * 
 * @param limit Number of articles to fetch
 * @returns Promise resolving to the fetched data
 */
export const prefetchLearningArticles = async (
  limit: number = 5
): Promise<LearningArticle[]> => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  
  if (!supabaseUrl) {
    throw new Error('Supabase URL not configured');
  }

  const apiUrl = `${supabaseUrl}/functions/v1/public-learning-content?action=articles&limit=${limit}`;
  
  try {
    return await fetchLearningArticles(apiUrl);
  } catch (error) {
    console.error('Failed to prefetch learning articles:', error);
    throw error;
  }
};

/**
 * Utility hook for getting a specific learning article by ID
 * This uses the same cache as the main hook but filters for a specific item
 * 
 * @param id The ID of the article to retrieve
 * @param options SWR options
 * @returns Single article or undefined
 */
export const useLearningArticle = (
  id: string,
  options: UseLearningArticlesOptions = {}
) => {
  const { data, error, isLoading, isValidating, revalidate } = useLearningArticles(options);
  
  const article = data?.find(item => item.id === id);
  
  return {
    data: article,
    error,
    isLoading,
    isValidating,
    revalidate,
  };
};

export default useLearningArticles;